import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabase';
import { getThisWeekSnapshot } from '../../../services/historyService';
import { loadWeeklySessionTarget } from '../../../services/weeklyTargetService';
import { colors, typography, radius, spacing } from '../../../theme';

/**
 * This Week — compact consolidated card.
 * Ring = sessions this week vs weekly target (adapted from ConsistencyScoreCard,
 * with the "last 4 weeks" history strip dropped — that's covered by Training
 * Calendar instead) + a weekly-volume line from getThisWeekSnapshot.
 *
 * Uses a single Monday-based week boundary (getThisWeekSnapshot's) for both the
 * day count and volume, rather than mixing in ConsistencyScoreCard's separate
 * Sunday-based day count — avoids shipping two different "this week" definitions
 * on one card.
 */
const ThisWeekCard = ({ userId, programId }) => {
  const [loading, setLoading] = useState(true);
  const [weekTarget, setWeekTarget] = useState(0);
  const [snapshot, setSnapshot] = useState({ sessions: 0, volume: 0 });

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [snap, target] = await Promise.all([
          getThisWeekSnapshot(userId),
          programId ? fetchWeekTarget(programId) : Promise.resolve(0),
        ]);
        if (cancelled) return;
        setSnapshot(snap);
        setWeekTarget(target);
      } catch (err) {
        console.error('Error loading This Week card:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, programId]);

  // The days of the week the client is on, not an average over every week made: a day added to a later
  // week must not count against this one (Codex's review 032, R4). An assignment without a week is on
  // week 1, as the client's screen takes it. No answer, no active assignment or no days that week: no
  // target (0), never the other weeks' average in its place (Codex's review 033, F2).
  const fetchWeekTarget = async (programId) => {
    try {
      const { data: assignment, error } = await supabase
        .from('program_assignments')
        .select('current_week')
        .eq('user_id', userId)
        .eq('program_id', programId)
        .eq('is_active', true)
        .maybeSingle();
      if (error || !assignment) return 0;
      const target = await loadWeeklySessionTarget(programId, { week: assignment.current_week ?? 1 });
      return Math.round(target || 0);
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ ...typography.caption, color: colors.textMuted, textAlign: 'center', padding: spacing.lg }}>
          Loading…
        </div>
      </div>
    );
  }

  const { sessions: completedThisWeek, volume } = snapshot;
  const exceeded = weekTarget > 0 && completedThisWeek > weekTarget;
  const weekPct = weekTarget > 0
    ? Math.min(Math.round((completedThisWeek / weekTarget) * 100), 100)
    : 0;

  const ringColor = exceeded || weekPct >= 100
    ? colors.success
    : weekPct >= 50
      ? colors.warning
      : weekTarget === 0
        ? colors.textSubtle
        : colors.danger;

  let statusText, statusColor;
  if (weekTarget === 0) {
    statusText = 'No weekly target set.';
    statusColor = colors.textMuted;
  } else if (exceeded) {
    statusText = `${completedThisWeek} sessions this week — beyond target!`;
    statusColor = colors.success;
  } else if (weekPct >= 100) {
    statusText = 'Target hit. Great work this week.';
    statusColor = colors.success;
  } else if (completedThisWeek > 0) {
    const remaining = weekTarget - completedThisWeek;
    statusText = `${remaining} more session${remaining !== 1 ? 's' : ''} to hit your weekly target.`;
    statusColor = colors.warning;
  } else {
    statusText = `Start your ${weekTarget} session${weekTarget !== 1 ? 's' : ''} this week!`;
    statusColor = colors.textMuted;
  }

  // Compact ring — smaller than ConsistencyScoreCard's original 200px
  const size = 130;
  const sw = 12;
  const radiusPx = (size - sw) / 2;
  const circ = 2 * Math.PI * radiusPx;
  const offset = circ - (weekPct / 100) * circ;

  return (
    <div style={styles.container}>
      <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.md }}>
        THIS WEEK
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
          <svg width={size} height={size} style={{ display: 'block' }}>
            <circle cx={size / 2} cy={size / 2} r={radiusPx} fill="none" stroke={colors.surface2} strokeWidth={sw} />
            <circle
              cx={size / 2} cy={size / 2} r={radiusPx} fill="none"
              stroke={ringColor} strokeWidth={sw}
              strokeDasharray={circ} strokeDashoffset={offset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              className="stats-motion"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            textAlign: 'center', userSelect: 'none',
          }}>
            <div style={{ fontSize: '28px', fontWeight: '700', lineHeight: 1, color: ringColor }}>
              {completedThisWeek} / {weekTarget}
            </div>
            <div style={{ ...typography.label, color: colors.textMuted, marginTop: '4px', fontSize: '11px' }}>
              {weekPct >= 100 ? 'TARGET COMPLETE' : 'SESSIONS'}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <div>
              <div style={{ ...typography.sectionHeading, fontSize: '18px', color: colors.text }}>
                {completedThisWeek}
              </div>
              <div style={{ ...typography.label, color: colors.textMuted }}>SESSIONS COMPLETED</div>
            </div>
            <div>
              <div style={{ ...typography.sectionHeading, fontSize: '18px', color: colors.text }}>
                {volume > 0 ? `${(volume / 1000).toFixed(1)}k kg` : '0 kg'}
              </div>
              <div style={{ ...typography.label, color: colors.textMuted }}>WEEKLY VOLUME</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: spacing.lg, paddingTop: spacing.md, borderTop: `1px solid ${colors.border}` }}>
        <span style={{ ...typography.caption, color: statusColor, fontWeight: '600' }}>
          {statusText}
        </span>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: spacing.lg,
    background: colors.surface1,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.card,
    marginBottom: spacing.lg,
  },
};

export default ThisWeekCard;
